import { onAuthStateChanged, User } from "firebase/auth";
import { useEffect, useState } from "react";
import "./App.css";
import { getAuthToken } from "./auth/composable";
import { firebaseAuth } from "./auth/firebase";
import SigninPanel from "./SigninScreen";
import { datadogLogs } from "@datadog/browser-logs";

function App() {
    const qs = new URLSearchParams(window.location.search);
    const [redirectUri, setRedirectUri] = useState(qs.get("redirect_uri"));
    const [stsEndpoint] = useState<string | null>(qs.get("sts"));
    const [state, setState] = useState(qs.get("state"));
    const [user, setUser] = useState<User | null>(null);
    const [composableToken, setComposableToken] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined);
    const [debug] = useState<boolean>(qs.get("debug") ? true : false);
    const [fbToken, setFbToken] = useState<string | undefined>(undefined);

    console.log("rendering app");
    datadogLogs.logger.info("Rendering app");

    const doRedirect = (token: string) => {
        console.log("Redirecting...", redirectUri, state);
        datadogLogs.logger.info("Redirecting...", {
            vertesia: {
                redirect_uri: redirectUri,
                state: state,
            },
        });

        if (!state) {
            datadogLogs.logger.error("No state set", {
                vertesia: {
                    redirect_uri: redirectUri,
                    state: state,
                },
            });
            setError("No state set");
            return;
        }

        if (!redirectUri) {
            setError("No redirect target set");
            datadogLogs.logger.error("No redirect target set", {
                vertesia: {
                    redirect_uri: redirectUri,
                    state: state,
                },
            });
            return;
        }

        console.log("Redirecting to: ", redirectUri);
        datadogLogs.logger.info("Redirecting to...", {
            vertesia: {
                redirect_uri: redirectUri,
                state: state,
            },
        });
        const url = new URL(redirectUri);
        url.hash = new URLSearchParams({
            token,
            state,
        }).toString();
        window.location.assign(url.toString());
    };

    //get redirect target from URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTarget = urlParams.get("redirect_uri");
        const state = urlParams.get("state");
        if (state) {
            setState(state);
            console.log("State: ", state);
            datadogLogs.logger.info(`State: ${state}`, {
                vertesia: {
                    redirect_uri: redirectUri,
                    state: state,
                },
            });
        } else {
            console.error("No state set in URL");
            datadogLogs.logger.error("No state set in URL", {
                vertesia: {
                    redirect_uri: redirectUri,
                    state: state,
                },
            });
        }
        if (redirectTarget) {
            setRedirectUri(redirectTarget);
            console.log("Redirect target: ", redirectTarget);
            datadogLogs.logger.info(`Redirect target: ${redirectTarget}`, {
                vertesia: {
                    redirect_uri: redirectUri,
                    state: state,
                },
            });
        }
    }, [window.location.search]);

    useEffect(() => {
        return onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
            if (firebaseUser) {
                console.log("Auth: successful login with firebase");
                datadogLogs.logger.info("Successful login with firebase");

                setUser(firebaseUser);
                firebaseUser
                    .getIdToken()
                    .then((t) => {
                        setFbToken(t);
                        return getAuthToken(t, stsEndpoint);
                    })
                    .then((token) => {
                        if (!token) {
                            console.error("Failed to get token");
                            datadogLogs.logger.error("Failed to get token");
                            return;
                        }
                        setComposableToken(token);
                        doRedirect(token);
                    });
            } else {
                console.log("Auth: no user");
                datadogLogs.logger.info("No user");
                setUser(null);
            }
        });
    }, []);

    return (
        <div>
            <h1 className="text-xl font-semibold py-8">Welcome to Vertesia Cloud Central Auth</h1>

            <p>Redirect Target has been set to: {redirectUri ?? "no redirect target"} </p>
            <p>STS Endpoint: {stsEndpoint ?? "default sts endpoint"} </p>

            {user && <div>You are logged in as {user.email}</div>}
            {debug && (
                <div className="flex flex-col gap-y-4">
                    <code className="border flex-wrap text-wrap overflow-auto">User: {JSON.stringify({ user })}</code>
                    <code className="border flex-wrap text-wrap overflow-auto">FB Token: {fbToken} </code>
                </div>
            )}
            {composableToken && (
                <div>
                    Composable Token acquired.
                    {redirectUri ? <p>Redirecting to {redirectUri}</p> : <p>No redirect target set</p>}
                </div>
            )}

            {!user && redirectUri && <SigninPanel redirectTo={redirectUri} />}

            {error && (
                <div>
                    <p>Error: {error}</p>
                </div>
            )}
        </div>
    );
}

export default App;
